import { useState } from "react";
import axios from "axios";
import type {
    CreateUploadUrlDto,
    CreateDocumentDto,
    UserDtoDocumentsInnerTypeEnum,
} from "../../../api/api";
import { getDocumentDisplayName } from "../../../utils/onboarding";
import { documentsService } from "../../api_client";
import { showErrorToast, showSuccessToast } from "../../../utils/toasts";

export function useDocumentUploadForUser() {
    const [ uploading, setUploading ] = useState<
        Record<UserDtoDocumentsInnerTypeEnum, boolean>
    >({
        CR: false,
        TAX_CARD: false,
    });

    const [ selectedFiles, setSelectedFiles ] = useState<
        Record<UserDtoDocumentsInnerTypeEnum, File | null>
    >({
        CR: null,
        TAX_CARD: null,
    });

    const [ uploadedDocuments, setUploadedDocuments ] = useState<
        Record<UserDtoDocumentsInnerTypeEnum, boolean>
    >({
        CR: false,
        TAX_CARD: false,
    });

    const [ uploadProgress, setUploadProgress ] = useState<
        Record<UserDtoDocumentsInnerTypeEnum, number>
    >({
        CR: 0,
        TAX_CARD: 0,
    });

    const handleFileSelect = (
        type: UserDtoDocumentsInnerTypeEnum,
        file: File | null
    ) => {
        if (!file) {
            setSelectedFiles((prev) => ({ ...prev, [ type ]: null }));
            setUploadedDocuments((prev) => ({ ...prev, [ type ]: false }));
            return;
        }

        setSelectedFiles((prev) => ({ ...prev, [ type ]: file }));
        setUploadedDocuments((prev) => ({ ...prev, [ type ]: false }));
    };

    const handleFileRemove = (type: UserDtoDocumentsInnerTypeEnum) => {
        setSelectedFiles((prev) => ({ ...prev, [ type ]: null }));
        setUploadedDocuments((prev) => ({ ...prev, [ type ]: false }));
    };

    const handleConfirmUpload = async (
        type: UserDtoDocumentsInnerTypeEnum,
        targetUserId: string
    ) => {
        const file = selectedFiles[ type ];
        if (!file) return;

        setUploading((prev) => ({ ...prev, [ type ]: true }));
        setUploadProgress((prev) => ({ ...prev, [ type ]: 0 }));

        try {
            // Step 1: Get upload URL
            const uploadUrlRequest: CreateUploadUrlDto = {
                type,
                contentTypes: [ file.type ],
            };

            const uploadUrlResponse = await documentsService.createDocumentUploadUrl(
                uploadUrlRequest
            );
            const uploadUrlData = uploadUrlResponse.data[ 0 ];

            if (!uploadUrlData) {
                throw new Error("Failed to get upload URL");
            }

            // Step 2: Upload file to storage with real progress
            // Do NOT set Content-Length - it's a forbidden header; the browser sets it automatically.
            const headers: Record<string, string> = {
                ...(uploadUrlData.headers as Record<string, string>),
            };

            if (!headers[ "Content-Type" ]) {
                headers[ "Content-Type" ] = file.type;
            }

            await axios.put(uploadUrlData.uploadUrl, file, {
                headers,
                onUploadProgress: (event) => {
                    if (!event.total) return;
                    const progress = event.loaded / event.total; // 0–1
                    setUploadProgress((prev) => ({
                        ...prev,
                        [ type ]: progress,
                    }));
                },
            });

            setUploadProgress((prev) => ({ ...prev, [ type ]: 1 }));

            // Step 3: Create document record for the user
            const createDocumentRequest: CreateDocumentDto = {
                type,
                objectKey: uploadUrlData.objectKey,
                fileName: file.name,
                contentType: file.type,
                fileSize: file.size,
                userId: targetUserId, // Specify the user ID for admin uploads
            };

            await documentsService.createDocument(createDocumentRequest);

            showSuccessToast(`${getDocumentDisplayName(type)} uploaded successfully!`);

            // Clear selected file after successful upload
            setSelectedFiles((prev) => ({ ...prev, [ type ]: null }));
        } catch (error) {
            console.error("Upload error:", error);
            showErrorToast(
                `Failed to upload ${getDocumentDisplayName(type)}. Please try again.`
            );
        } finally {
            setUploading((prev) => ({ ...prev, [ type ]: false }));
        }
    };

    // Prepare documents for user creation (uploads files and returns CreateDocumentDto array)
    const prepareDocumentsForCreation = async (): Promise<CreateDocumentDto[]> => {
        const filesToUpload: Array<{ type: UserDtoDocumentsInnerTypeEnum; file: File }> = [];

        for (const [ type, file ] of Object.entries(selectedFiles) as [
            UserDtoDocumentsInnerTypeEnum,
            File | null
        ][]) {
            if (file) {
                filesToUpload.push({ type, file });
            }
        }

        if (filesToUpload.length === 0) {
            return [];
        }

        const documentDtos: CreateDocumentDto[] = [];

        // Upload each file sequentially to avoid race conditions
        for (const { type, file } of filesToUpload) {
            setUploading((prev) => ({ ...prev, [ type ]: true }));
            setUploadProgress((prev) => ({ ...prev, [ type ]: 0 }));

            try {
                // Step 1: Get upload URL
                const uploadUrlRequest: CreateUploadUrlDto = {
                    type,
                    contentTypes: [ file.type ],
                };

                const uploadUrlResponse = await documentsService.createDocumentUploadUrl(
                    uploadUrlRequest
                );
                const uploadUrlData = uploadUrlResponse.data[ 0 ];

                if (!uploadUrlData) {
                    throw new Error("Failed to get upload URL");
                }

                // Step 2: Upload file to storage with real progress
                // Do NOT set Content-Length - it's a forbidden header; the browser sets it automatically.
                const headers: Record<string, string> = {
                    ...(uploadUrlData.headers as Record<string, string>),
                };

                if (!headers[ "Content-Type" ]) {
                    headers[ "Content-Type" ] = file.type;
                }

                await axios.put(uploadUrlData.uploadUrl, file, {
                    headers,
                    onUploadProgress: (event) => {
                        if (!event.total) return;
                        const progress = event.loaded / event.total; // 0–1
                        setUploadProgress((prev) => ({
                            ...prev,
                            [ type ]: progress,
                        }));
                    },
                });

                setUploadProgress((prev) => ({ ...prev, [ type ]: 1 }));

                // Step 3: Create CreateDocumentDto (without userId - will be set by backend)
                const createDocumentDto: CreateDocumentDto = {
                    type,
                    objectKey: uploadUrlData.objectKey,
                    fileName: file.name,
                    contentType: file.type,
                    fileSize: file.size,
                };

                documentDtos.push(createDocumentDto);
                // Mark as uploaded
                setUploadedDocuments((prev) => ({ ...prev, [ type ]: true }));
            } catch (error) {
                console.error(`Upload error for ${type}:`, error);
                showErrorToast(
                    `Failed to upload ${getDocumentDisplayName(type)}. Please try again.`
                );
                throw error;
            } finally {
                setUploading((prev) => ({ ...prev, [ type ]: false }));
            }
        }

        return documentDtos;
    };

    return {
        uploading,
        uploadProgress,
        selectedFiles,
        uploadedDocuments,
        handleFileSelect,
        handleFileRemove,
        handleConfirmUpload,
        prepareDocumentsForCreation,
    };
}


